import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Product } from "@shared/types";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { AdminLayout } from "./AdminLayout";
import { Button } from "@/components/Button";

export function AdminProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.admin.products.list().then(setProducts).finally(() => setLoading(false));
  useEffect(() => {
    load();
  }, []);

  const remove = async (id: string) => {
    if (!confirm("Delete this product? This cannot be undone.")) return;
    await api.admin.products.remove(id);
    load();
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-display text-xl font-medium text-ink">Products</h1>
        <Link to="/admin/products/new">
          <Button>+ New Product</Button>
        </Link>
      </div>

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : products.length === 0 ? (
        <p className="text-sm text-stone-500">No products yet. Add your first one.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-stone-300 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-stone-300 text-xs uppercase tracking-wide text-stone-500">
              <tr>
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Stock</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {products.map((p) => (
                <tr key={p.id}>
                  <td className="flex items-center gap-3 px-4 py-3">
                    <div className="h-10 w-10 overflow-hidden rounded bg-stone-100">
                      {p.images[0] && <img src={p.images[0].url} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <span className="font-medium text-ink">{p.name}</span>
                  </td>
                  <td className="px-4 py-3 text-stone-700">{p.categoryName ?? "—"}</td>
                  <td className="px-4 py-3 text-stone-700">{formatMoney(p.priceCents, p.currency)}</td>
                  <td className="px-4 py-3 text-stone-700">{p.stockQty}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded px-2 py-1 text-xs font-semibold ${p.isActive ? "bg-success/10 text-success" : "bg-stone-100 text-stone-500"}`}
                    >
                      {p.isActive ? "Active" : "Hidden"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/admin/products/${p.id}`} className="mr-3 text-xs font-semibold text-ink">
                      Edit
                    </Link>
                    <button onClick={() => remove(p.id)} className="text-xs font-semibold text-error">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
